import Component from "@glimmer/component";
import { action } from "@ember/object";
import { fn } from "@ember/helper";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { eq } from "discourse/truth-helpers";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function parsedCategoryIds(value) {
  return `${value || ""}`
    .split(",")
    .map((item) => parseInt(item.trim(), 10))
    .filter((item) => Number.isInteger(item) && item > 0)
    .filter((item, index, list) => list.indexOf(item) === index);
}

const PROVIDER_LABELS = {
  google: "Google Calendar",
  edgeos: "EdgeOS",
};

function connectionView(connection, attrs = {}) {
  const categoryIds = asArray(attrs.sync_category_ids || connection.sync_category_ids);
  const providerLabel =
    PROVIDER_LABELS[connection.provider] || connection.provider || "External calendar";
  const syncLabel = connection.last_sync_at
    ? `Last synced ${new Date(connection.last_sync_at).toLocaleString()}`
    : "Not synced yet";
  const summary = connection.sync_summary;
  const syncSummaryLabel =
    summary && Number.isInteger(summary.created)
      ? `${summary.rsvps_seen ?? 0} RSVPs found, ${summary.created} added, ${summary.removed ?? 0} removed`
      : "";
  const syncWarningLabel =
    summary?.complete === false
      ? "The last EdgeOS sync reached a safety limit. Existing RSVPs were preserved; sync coverage is incomplete."
      : "";

  return {
    ...connection,
    ...attrs,
    provider_label: providerLabel,
    sync_label: syncLabel,
    sync_summary_label: syncSummaryLabel,
    sync_warning_label: syncWarningLabel,
    is_edgeos: connection.provider === "edgeos",
    is_google: connection.provider === "google",
    sync_category_ids: categoryIds,
    sync_category_ids_label: categoryIds.join(", "),
    notification_level: attrs.notification_level || connection.notification_level || "material",
  };
}

export default class ExternalCalendarsPreferences extends Component {
  @service siteSettings;

  @tracked connections = [];
  @tracked isLoading = true;
  @tracked notice = "";
  @tracked error = "";
  @tracked savingConnectionId = null;
  @tracked syncingConnectionId = null;
  @tracked disconnectingConnectionId = null;
  @tracked showEdgeosForm = false;
  @tracked edgeosApiKey = "";
  @tracked isConnectingEdgeos = false;
  @tracked googleConfigured = false;

  constructor() {
    super(...arguments);
    this.loadConnections();
  }

  get hasConnections() {
    return this.connections.length > 0;
  }

  get edgeosEnabled() {
    return this.siteSettings.discourse_events_calendar_edgeos_rsvp_sync_enabled === true;
  }

  get hasEdgeosConnection() {
    return this.connections.some((connection) => connection.provider === "edgeos");
  }

  get canConnectEdgeos() {
    return this.edgeosEnabled && !this.hasEdgeosConnection;
  }

  async loadConnections() {
    this.isLoading = true;
    this.error = "";

    try {
      const response = await ajax("/calendar/external/connections.json");
      this.connections = asArray(response?.connections).map((connection) =>
        connectionView(connection)
      );
      this.googleConfigured = response?.google_configured === true;
    } catch (error) {
      this.error = "External calendars could not be loaded.";
      popupAjaxError(error);
    } finally {
      this.isLoading = false;
    }
  }

  replaceConnection(connection) {
    const nextConnection = connectionView(connection);
    this.connections = this.connections.map((existing) =>
      `${existing.id}` === `${nextConnection.id}` ? nextConnection : existing
    );
  }

  updateConnection(connection, attrs) {
    this.replaceConnection(connectionView(connection, attrs));
  }

  @action
  setSyncEnabled(connection, event) {
    connection.sync_enabled = event.target.checked;
    this.connections = [...this.connections];
  }

  @action
  setCategoryIds(connection, event) {
    const label = event.target.value;
    connection.sync_category_ids = parsedCategoryIds(label);
    connection.sync_category_ids_label = label;
    this.connections = [...this.connections];
  }

  @action
  setNotificationLevel(connection, event) {
    connection.notification_level = event.target.value;
    this.connections = [...this.connections];
  }

  @action
  async saveConnection(connection) {
    this.savingConnectionId = connection.id;
    this.notice = "";
    this.error = "";

    try {
      const response = await ajax(`/calendar/external/connections/${connection.id}.json`, {
        type: "PUT",
        data: {
          connection: {
            sync_enabled: connection.sync_enabled,
            sync_category_ids: connection.sync_category_ids,
            notification_level: connection.notification_level,
          },
        },
      });
      this.replaceConnection(response.connection);
      this.notice = "External calendar settings saved.";
    } catch (error) {
      this.error = "External calendar settings could not be saved.";
      popupAjaxError(error);
    } finally {
      this.savingConnectionId = null;
    }
  }

  @action
  async syncConnection(connection) {
    this.syncingConnectionId = connection.id;
    this.notice = "";
    this.error = "";

    try {
      const response = await ajax(
        `/calendar/external/connections/${connection.id}/sync_now.json`,
        { type: "POST" }
      );
      this.replaceConnection(response.connection);
      this.notice = response.already_running
        ? "Sync already in progress."
        : "Sync queued.";
    } catch (error) {
      this.error = "External calendar sync could not be queued.";
      popupAjaxError(error);
    } finally {
      this.syncingConnectionId = null;
    }
  }

  @action
  toggleEdgeosForm() {
    this.showEdgeosForm = !this.showEdgeosForm;
    this.notice = "";
    this.error = "";
  }

  @action
  setEdgeosApiKey(event) {
    this.edgeosApiKey = event.target.value;
  }

  @action
  async connectEdgeos(event) {
    event?.preventDefault?.();

    const apiKey = this.edgeosApiKey.trim();
    if (!apiKey) {
      this.error = "Paste your EdgeOS API key first.";
      return;
    }

    this.isConnectingEdgeos = true;
    this.notice = "";
    this.error = "";

    try {
      const response = await ajax("/calendar/external/connections.json", {
        type: "POST",
        data: { provider: "edgeos", api_key: apiKey },
      });
      const nextConnection = connectionView(response.connection);
      this.connections = [
        ...this.connections.filter(
          (existing) => `${existing.id}` !== `${nextConnection.id}`
        ),
        nextConnection,
      ];
      this.edgeosApiKey = "";
      this.showEdgeosForm = false;
      this.notice = "EdgeOS connected. Your RSVPs are syncing now.";
    } catch (error) {
      this.error =
        error?.jqXHR?.responseJSON?.errors?.[0] ||
        "EdgeOS could not be connected. Check the API key.";
    } finally {
      this.isConnectingEdgeos = false;
    }
  }

  @action
  async disconnectConnection(connection) {
    const message =
      connection.provider === "edgeos"
        ? "Disconnect EdgeOS? RSVPs synced from EdgeOS will be removed from your calendar."
        : `Disconnect ${connection.provider_label}?`;
    if (!window.confirm(message)) {
      return;
    }

    this.disconnectingConnectionId = connection.id;
    this.notice = "";
    this.error = "";

    try {
      await ajax(`/calendar/external/connections/${connection.id}.json`, {
        type: "DELETE",
      });
      this.connections = this.connections.filter(
        (existing) => `${existing.id}` !== `${connection.id}`
      );
      this.notice = "External calendar disconnected.";
    } catch (error) {
      this.error = "External calendar could not be disconnected.";
      popupAjaxError(error);
    } finally {
      this.disconnectingConnectionId = null;
    }
  }

  <template>
    <section class="events-calendar-preferences">
      <header class="events-calendar-preferences__header">
        <div>
          <h2>External calendars</h2>
          <p>Connect external accounts to sync calendars and RSVPs.</p>
        </div>
        <div class="events-calendar-preferences__header-actions">
          {{#if this.canConnectEdgeos}}
            <button
              type="button"
              class="btn btn-default"
              {{on "click" this.toggleEdgeosForm}}
            >
              Connect EdgeOS
            </button>
          {{/if}}
          {{#if this.googleConfigured}}
            <a
              href="/calendar/external/connect/google"
              class="btn btn-primary"
              data-auto-route="false"
            >
              Connect Google Calendar
            </a>
          {{/if}}
        </div>
      </header>

      {{#if this.showEdgeosForm}}
        <form
          class="events-calendar-preferences__edgeos-form"
          {{on "submit" this.connectEdgeos}}
        >
          <label for="edgeos-api-key">EdgeOS API key</label>
          <div class="events-calendar-preferences__edgeos-form-row">
            <input
              id="edgeos-api-key"
              type="password"
              autocomplete="off"
              value={{this.edgeosApiKey}}
              placeholder="Paste your EdgeOS API key"
              {{on "input" this.setEdgeosApiKey}}
            />
            <button
              type="submit"
              class="btn btn-primary"
              disabled={{this.isConnectingEdgeos}}
            >
              {{#if this.isConnectingEdgeos}}Connecting...{{else}}Connect{{/if}}
            </button>
          </div>
          <small>
            Create a key in the EdgeOS portal under API keys with the
            <code>events:read</code> scope. Your RSVPs will appear under "My
            RSVPs" in the calendar and sync hourly.
          </small>
        </form>
      {{/if}}

      {{#if this.error}}
        <div class="alert alert-error" role="alert">{{this.error}}</div>
      {{/if}}

      {{#if this.notice}}
        <div class="alert alert-info" role="status">{{this.notice}}</div>
      {{/if}}

      {{#if this.isLoading}}
        <p class="events-calendar-preferences__empty">Loading external calendars...</p>
      {{else}}
        {{#if this.hasConnections}}
          <div class="events-calendar-preferences__list">
            {{#each this.connections as |connection|}}
              <article
                class="events-calendar-preferences__item
                  {{if connection.is_google 'events-calendar-preferences__item--settings'}}"
              >
                <div class="events-calendar-preferences__item-main">
                  <h3>{{connection.provider_label}}</h3>
                  {{#unless connection.is_edgeos}}
                    {{#if connection.provider_email}}
                      <p>{{connection.provider_email}}</p>
                    {{else}}
                      <p>{{connection.provider_user_id}}</p>
                    {{/if}}
                  {{/unless}}
                  <span>{{connection.sync_label}}</span>
                  {{#if connection.reauthorization_required}}
                    <p class="alert alert-warning" role="alert">
                      Reconnect Google Calendar before syncing. This confirms the
                      verified Google address used for invitations and RSVP replies.
                    </p>
                  {{/if}}
                  {{#if connection.is_edgeos}}
                    {{#if connection.sync_error}}
                      <p class="events-calendar-preferences__sync-error">
                        Last sync failed: {{connection.sync_error}}
                      </p>
                    {{else if connection.sync_warning_label}}
                      <p class="alert alert-warning" role="alert">
                        {{connection.sync_warning_label}}
                      </p>
                      {{#if connection.sync_summary_label}}
                        <p class="events-calendar-preferences__sync-summary">
                          {{connection.sync_summary_label}}
                        </p>
                      {{/if}}
                    {{else if connection.sync_summary_label}}
                      <p class="events-calendar-preferences__sync-summary">
                        {{connection.sync_summary_label}}
                      </p>
                    {{/if}}
                    <p class="events-calendar-preferences__provider-hint">
                      Your EdgeOS RSVPs sync into "My RSVPs" on the calendar.
                      Syncing is one-way; RSVP changes are made in EdgeOS.
                    </p>
                  {{/if}}
                </div>

                {{#if connection.is_google}}
                <div class="events-calendar-preferences__settings">
                  <label class="events-calendar-preferences__checkbox">
                    <input
                      type="checkbox"
                      checked={{connection.sync_enabled}}
                      {{on "change" (fn this.setSyncEnabled connection)}}
                    />
                    Sync enabled
                  </label>

                  <label>
                    <span>Categories</span>
                    <input
                      type="text"
                      value={{connection.sync_category_ids_label}}
                      placeholder="All event categories"
                      {{on "input" (fn this.setCategoryIds connection)}}
                    />
                    <small>Comma-separated category IDs. Leave blank for all categories.</small>
                  </label>

                  <label>
                    <span>Direction</span>
                    <select disabled>
                      <option>One-way + RSVP replies</option>
                      <option>Two-way sync unavailable in v2.0</option>
                    </select>
                  </label>

                  <label>
                    <span>Notifications</span>
                    <select {{on "change" (fn this.setNotificationLevel connection)}}>
                      <option
                        value="every"
                        selected={{eq connection.notification_level "every"}}
                      >
                        Every change
                      </option>
                      <option
                        value="material"
                        selected={{eq connection.notification_level "material"}}
                      >
                        Material changes only
                      </option>
                      <option
                        value="never"
                        selected={{eq connection.notification_level "never"}}
                      >
                        Never
                      </option>
                    </select>
                  </label>
                </div>
                {{/if}}

                <div class="events-calendar-preferences__actions">
                  {{#if connection.is_google}}
                  <button
                    type="button"
                    class="btn btn-primary"
                    disabled={{eq this.savingConnectionId connection.id}}
                    {{on "click" (fn this.saveConnection connection)}}
                  >
                    {{#if (eq this.savingConnectionId connection.id)}}
                      Saving...
                    {{else}}
                      Save settings
                    {{/if}}
                  </button>
                  {{/if}}
                  <button
                    type="button"
                    class="btn btn-default"
                    disabled={{eq this.syncingConnectionId connection.id}}
                    {{on "click" (fn this.syncConnection connection)}}
                  >
                    {{#if (eq this.syncingConnectionId connection.id)}}
                      Syncing...
                    {{else}}
                      Sync now
                    {{/if}}
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger"
                    disabled={{eq this.disconnectingConnectionId connection.id}}
                    {{on "click" (fn this.disconnectConnection connection)}}
                  >
                    {{#if (eq this.disconnectingConnectionId connection.id)}}
                      Disconnecting...
                    {{else}}
                      Disconnect
                    {{/if}}
                  </button>
                </div>
              </article>
            {{/each}}
          </div>
        {{else}}
          <p class="events-calendar-preferences__empty">
            No external calendars connected.
          </p>
        {{/if}}
      {{/if}}
    </section>
  </template>
}
